/**
 * Bookmark Button Component
 * Star icon button to bookmark/unbookmark messages
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useBookmarks } from '@/hooks/useBookmarks';

interface BookmarkButtonProps {
  messageId: string;
  size?: 'sm' | 'md';
  showLabel?: boolean;
}

export const BookmarkButton = ({ messageId, size = 'md', showLabel = false }: BookmarkButtonProps) => {
  const { bookmarks, toggleBookmark } = useBookmarks();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if message is bookmarked
  useEffect(() => {
    const bookmark = bookmarks.find(b => b.message_id === messageId);
    setIsBookmarked(!!bookmark);
  }, [bookmarks, messageId]);

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent handlers

    setIsLoading(true);
    try {
      await toggleBookmark(messageId);

      if (isBookmarked) {
        toast.success('Запазването е премахнато');
      } else {
        toast.success('Съобщението е запазено');
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      toast.error('Грешка при запазване на съобщението');
    } finally {
      setIsLoading(false);
    }
  };

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const buttonSize = size === 'sm' ? 'p-1' : 'p-2';

  return (
    <button
      onClick={handleToggle}
      disabled={isLoading}
      className={`${buttonSize} rounded hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5`}
      title={isBookmarked ? 'Премахни запазването' : 'Запази съобщението'}
    >
      {isBookmarked ? (
        // Filled star (bookmarked)
        <svg
          className={`${iconSize} text-yellow-500`}
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ) : (
        // Empty star (not bookmarked)
        <svg
          className={`${iconSize} text-gray-400 dark:text-dark-text-muted`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
          />
        </svg>
      )}

      {showLabel && (
        <span className="text-sm text-gray-600 dark:text-dark-text-secondary">
          {isBookmarked ? 'Запазено' : 'Запази'}
        </span>
      )}
    </button>
  );
};
